/**
 * PowerSync ↔ Supabase Connector — Renderer Process
 *
 * Runs in the renderer where the Supabase session lives (localStorage).
 * Responsibilities:
 *  1. fetchCredentials() — provides the PowerSync service with the current
 *     user's JWT so it can authorise the sync stream.
 *  2. uploadData()       — flushes the local write queue to Supabase whenever
 *     the device is online.
 */

import {
  AbstractPowerSyncDatabase,
  CrudEntry,
  PowerSyncBackendConnector,
  UpdateType,
} from '@powersync/web';

import {
  checkGracePeriod,
  classifyOrdersConflict,
  type ConflictResolutionOutcome,
  FATAL_RESPONSE_CODES,
  getRetryKey,
  hasRequiredBillConflictFields,
  IGNORE_DUPLICATES_ON_ID_TABLES,
  isNetworkConnectivityError,
  isSubscriptionPausedError,
  ORDER_CHILD_TABLE_CLEANUP_ORDER,
  OUTLET_ID_PK_TABLES,
  stripGeneratedColumns,
  SUBSCRIPTION_PAUSED_EVENT_THROTTLE_MS,
  UPLOAD_RETRY_GRACE_PERIOD_MS,
  UPSERT_ON_ORDER_ID_TABLES,
} from '@shared/powersync/connectorConflicts';
import { supabase } from '@/renderer/services/supabase';
import { getEnvVar } from '@/renderer/util';

const POWERSYNC_URL =
  (getEnvVar('POWERSYNC_URL') as string) ||
  'https://6a354cdedeeddd0df606286f.powersync.journeyapps.com';

// Lightweight event bus so the sync layer can notify the UI without creating
// a circular import between the connector and Redux / React.
export const syncEvents = new EventTarget();

const firstFailureAt = new Map<string | number, number>();

const SUBSCRIPTION_PAUSED_MESSAGE =
  "This account is paused, so changes can't be uploaded yet. Orders are saved on this device and will sync automatically once the account is reactivated.";
let lastSubscriptionPausedEventAt = 0;

// Throw an Error that preserves the Postgres error code/details from a
// PostgrestError. `code` is read by the FATAL_RESPONSE_CODES check in
// uploadData — a plain `new Error()` loses it and causes unrecoverable
// errors (e.g. 23505 duplicate key) to retry forever. `details` is read by
// classifyOrdersConflict to tell apart the two distinct UNIQUE constraints
// on `orders` (bill_number vs single-open-per-table).
function throwDbError(
  operation: string,
  table: string,
  error: { message: string; code?: string | null; details?: string | null },
): never {
  const e = new Error(`${operation} ${table}: ${error.message}`) as Error & {
    code?: string;
    details?: string;
  };
  if (error.code) e.code = error.code;
  if (error.details) e.details = error.details;
  throw e;
}

// Discards a permanently-failing transaction and removes the row(s) it was
// trying to upload from local SQLite, so no "zombie" row is left behind that
// can never reach the server. `table` always comes from our own fixed
// PowerSync schema (AppSchema.ts), never user input.
async function discardAndCleanup(
  database: AbstractPowerSyncDatabase,
  failedOp: CrudEntry | null,
  eventName: string,
  message: string,
): Promise<void> {
  if (failedOp && !OUTLET_ID_PK_TABLES.has(failedOp.table)) {
    const { table, id } = failedOp;
    try {
      await database.writeTransaction(async (wtx) => {
        if (table === 'orders') {
          // eslint-disable-next-line no-restricted-syntax
          for (const childTable of ORDER_CHILD_TABLE_CLEANUP_ORDER) {
            // eslint-disable-next-line no-await-in-loop
            await wtx.execute(`DELETE FROM ${childTable} WHERE order_id = ?`, [
              id,
            ]);
          }
          await wtx.execute('DELETE FROM orders WHERE id = ?', [id]);
        } else {
          await wtx.execute(`DELETE FROM ${table} WHERE id = ?`, [id]);
        }
      });
    } catch (cleanupErr) {
      console.error(
        '[PowerSync] Failed to clean up discarded row:',
        cleanupErr,
      );
    }
  }
  syncEvents.dispatchEvent(new CustomEvent(eventName, { detail: { message } }));
}

// idx_orders_single_open_per_table means two devices opened an order for
// the same table while both offline — a genuine double-booking, not a
// numbering glitch. Merge this device's order onto the surviving order for
// that table instead of losing its items. Returns an outcome instead of
// throwing — the caller decides whether to retry, complete-and-discard, or
// complete normally, so grace-period bookkeeping stays centralized in
// uploadData(). Declared before handleBillNumberConflict because that
// function can delegate to this one (see the newBillNumber == null branch
// below).
async function handleDuplicateTableConflict(
  database: AbstractPowerSyncDatabase,
  failedOp: CrudEntry,
  tx: { complete: () => Promise<void> },
): Promise<ConflictResolutionOutcome> {
  const losingOrderId = failedOp.id;
  const opData = (failedOp.opData ?? {}) as Record<string, unknown>;
  const tableId = opData.table_id;

  if (!tableId) {
    console.error(
      '[PowerSync] Duplicate-table conflict: opData missing table_id, will retry:',
      opData,
    );
    return 'retry';
  }

  const { data: winningOrderId, error } = await supabase.rpc(
    'resolve_duplicate_table_order',
    { p_losing_order_id: losingOrderId, p_table_id: tableId },
  );

  if (error) {
    console.error(
      '[PowerSync] Failed to resolve duplicate-table order, will retry:',
      error,
    );
    return 'retry';
  }

  if (!winningOrderId) {
    // No other OPEN order currently holds this table (e.g. the other order
    // settled or was cancelled) — retry the original insert, which should
    // now succeed since the table is free again.
    return 'retry';
  }

  await tx.complete();
  await database.writeTransaction(async (wtx) => {
    // Re-parent, don't delete: resolve_duplicate_table_order only moves rows
    // that already exist server-side at the moment it runs. Anything this
    // device created locally but hadn't uploaded yet (PowerSync's CRUD queue
    // is strictly FIFO, so item/settlement writes queued after the order
    // insert haven't even been attempted when the insert conflict is
    // detected) never got a chance to be re-parented — deleting it here
    // would destroy it outright instead of merging it. Retargeting order_id
    // locally is safe either way: if the row was already re-parented
    // server-side, this local UPDATE just catches the local copy up early;
    // if it's still queued-unsynced, this makes its eventual upload land on
    // the surviving order instead of failing or being orphaned.
    // eslint-disable-next-line no-restricted-syntax
    for (const childTable of ORDER_CHILD_TABLE_CLEANUP_ORDER) {
      // eslint-disable-next-line no-await-in-loop
      await wtx.execute(
        `UPDATE ${childTable} SET order_id = ? WHERE order_id = ?`,
        [winningOrderId, losingOrderId],
      );
    }
    await wtx.execute('DELETE FROM orders WHERE id = ?', [losingOrderId]);
  });

  syncEvents.dispatchEvent(
    new CustomEvent('table-order-merged', {
      detail: { losingOrderId, winningOrderId, tableId },
    }),
  );
  return 'resolved';
}

// A bill_number collision is a numbering label mismatch, not a real
// conflict — every relationship that matters (order_items, settlements,
// kitchen_tickets, accounting) references orders.id, never bill_number. So
// instead of discarding the order, ask the server for the next available
// number (authoritative, since only the server can see the true current max
// under lock) and keep the order. Returns an outcome instead of throwing —
// the caller decides whether to retry, complete-and-discard, or complete
// normally, so grace-period bookkeeping stays centralized in uploadData().
async function handleBillNumberConflict(
  database: AbstractPowerSyncDatabase,
  failedOp: CrudEntry,
  tx: { complete: () => Promise<void> },
): Promise<ConflictResolutionOutcome> {
  const orderId = failedOp.id;
  const opData = (failedOp.opData ?? {}) as Record<string, unknown>;

  // Defensive: every `orders` PUT payload always carries these fields (see
  // AppSchema.ts) — a partial/malformed opData should fail loud via the
  // normal retry path instead of sending `undefined` into the RPC.
  if (!hasRequiredBillConflictFields(opData)) {
    console.error(
      '[PowerSync] Bill-number conflict: opData missing required fields, will retry:',
      opData,
    );
    return 'retry';
  }

  const { data: newBillNumber, error } = await supabase.rpc(
    'reassign_order_bill_number',
    {
      p_order_id: orderId,
      p_table_id: opData.table_id ?? null,
      p_waiter_id: opData.waiter_id,
      p_mode: opData.mode,
      p_client_id: opData.client_id ?? null,
      p_created_by: opData.created_by ?? null,
      p_outlet_id: opData.outlet_id ?? null,
    },
  );

  if (error) {
    console.error(
      '[PowerSync] Failed to reassign bill number, will retry:',
      error,
    );
    return 'retry';
  }

  if (newBillNumber == null) {
    // The RPC found that the real conflict is actually a table
    // double-booking (idx_orders_single_open_per_table), not a bill-number
    // collision — the same insert can violate both constraints at once,
    // but Postgres's 23505 only names whichever it hits first. Retrying
    // the original insert would hit the exact same misclassified conflict
    // forever, so resolve it as a duplicate-table conflict right here
    // instead of bubbling up.
    console.warn(
      '[PowerSync] Bill-number conflict was actually a table double-booking, resolving as duplicate-table:',
      orderId,
    );
    return handleDuplicateTableConflict(database, failedOp, tx);
  }

  await tx.complete();
  await database.writeTransaction(async (wtx) => {
    await wtx.execute(
      'UPDATE orders SET bill_number = ?, is_bill_printed = 0, updated_at = ? WHERE id = ?',
      [newBillNumber, new Date().toISOString(), orderId],
    );
  });

  syncEvents.dispatchEvent(
    new CustomEvent('bill-renumbered', {
      detail: { orderId, newBillNumber },
    }),
  );
  return 'resolved';
}

async function processOperation(op: CrudEntry): Promise<void> {
  const { table, op: type, id, opData } = op;

  console.debug(`[PowerSync] upload ${type} → ${table} (${id})`);

  switch (type) {
    case UpdateType.PUT: {
      if (OUTLET_ID_PK_TABLES.has(table)) {
        const rest = { ...(opData ?? {}) } as Record<string, unknown>;
        delete rest.id;
        const row = stripGeneratedColumns(table, { outlet_id: id, ...rest });
        const { error } = await supabase
          .from(table)
          .upsert(row, { onConflict: 'outlet_id' });
        if (error) throwDbError('PUT', table, error);
      } else if (UPSERT_ON_ORDER_ID_TABLES.has(table)) {
        // These tables enforce a unique constraint on `order_id`, not `id`.
        // Upsert on the business key so we update the existing record instead
        // of inserting a duplicate that would violate the unique index.
        const row = stripGeneratedColumns(table, { id, ...opData });
        const { error } = await supabase
          .from(table)
          .upsert(row, { onConflict: 'order_id' });
        if (error) throwDbError('PUT', table, error);
      } else {
        const row = stripGeneratedColumns(table, { id, ...opData });
        const { error } = await supabase.from(table).upsert(row, {
          onConflict: 'id',
          ignoreDuplicates: IGNORE_DUPLICATES_ON_ID_TABLES.has(table),
        });
        if (error) throwDbError('PUT', table, error);
      }
      break;
    }

    case UpdateType.PATCH: {
      const patch = stripGeneratedColumns(table, opData ?? {});
      const pkCol = OUTLET_ID_PK_TABLES.has(table) ? 'outlet_id' : 'id';
      const { error } = await supabase.from(table).update(patch).eq(pkCol, id);
      if (error) throwDbError('PATCH', table, error);
      break;
    }

    case UpdateType.DELETE: {
      const pkCol = OUTLET_ID_PK_TABLES.has(table) ? 'outlet_id' : 'id';
      const { error } = await supabase.from(table).delete().eq(pkCol, id);
      if (error) throwDbError('DELETE', table, error);
      break;
    }

    default:
      console.warn(`[PowerSync] Unknown op type: ${type}`);
  }
}

export class SupabaseConnector implements PowerSyncBackendConnector {
  // eslint-disable-next-line class-methods-use-this
  async fetchCredentials() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error || !session) {
      console.warn('[PowerSync] fetchCredentials: no active session');
      return null;
    }

    return {
      endpoint: POWERSYNC_URL,
      token: session.access_token,
      expiresAt: new Date(session.expires_at! * 1000),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    const tx = await database.getNextCrudTransaction();
    if (!tx) return;

    let lastOp: CrudEntry | null = null;
    try {
      await tx.crud.reduce(async (prev, op) => {
        await prev;
        lastOp = op;
        return processOperation(op);
      }, Promise.resolve() as Promise<void>);

      await tx.complete();
    } catch (ex: unknown) {
      const { code } = ex as { code?: string };
      // TypeScript's CFA infers lastOp as null here because the assignment
      // `lastOp = op` is inside an async callback — it can't guarantee the
      // assignment ran before the throw. Double-assert through unknown to
      // restore the correct CrudEntry | null type for narrowing below.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const failedOp = lastOp as unknown as CrudEntry | null;
      const exMessage = ex instanceof Error ? ex.message : String(ex);

      // Pure connectivity failure — the device is offline, plain and simple.
      // Never counts against the grace period below; just let PowerSync's
      // own retry loop keep trying, for as long as it takes to reconnect.
      if (isNetworkConnectivityError(exMessage)) {
        console.warn(
          '[PowerSync] Upload deferred — no network connectivity, will retry once online:',
          exMessage,
        );
        throw ex;
      }

      const retryKey = getRetryKey(tx.transactionId, failedOp);

      // Company disabled / subscription paused — the write is valid and will
      // succeed once the company is re-enabled. Never discard it (see
      // isSubscriptionPausedError): keep the queue blocked and retry
      // indefinitely, without counting against the grace period.
      if (isSubscriptionPausedError(exMessage)) {
        firstFailureAt.delete(retryKey);
        const now = Date.now();
        if (
          now - lastSubscriptionPausedEventAt >=
          SUBSCRIPTION_PAUSED_EVENT_THROTTLE_MS
        ) {
          lastSubscriptionPausedEventAt = now;
          console.warn(
            '[PowerSync] Upload paused — company disabled or subscription paused, will retry:',
            exMessage,
          );
          syncEvents.dispatchEvent(
            new CustomEvent('sync-paused', {
              detail: { message: SUBSCRIPTION_PAUSED_MESSAGE },
            }),
          );
        }
        throw ex;
      }

      // orders carries two independent UNIQUE constraints that both surface
      // as a plain 23505 — tell them apart by the constraint name embedded
      // in the Postgres error message (see classifyOrdersConflict) and
      // resolve each without ever destroying a real order.
      if (
        code === '23505' &&
        failedOp !== null &&
        failedOp.table === 'orders'
      ) {
        const conflictKind = classifyOrdersConflict(
          ex as { message?: string; details?: string },
        );

        if (
          conflictKind === 'bill-number' ||
          conflictKind === 'duplicate-table'
        ) {
          const outcome =
            conflictKind === 'bill-number'
              ? await handleBillNumberConflict(database, failedOp, tx)
              : await handleDuplicateTableConflict(database, failedOp, tx);

          if (outcome === 'resolved') {
            firstFailureAt.delete(retryKey);
            return;
          }

          // outcome === 'retry': do NOT clear firstFailureAt here — the
          // grace period measures time since the FIRST failure and must
          // survive across retries, otherwise a conflict that can never be
          // resolved this way retries forever (see connectorConflicts.ts).
          const { expired } = checkGracePeriod(firstFailureAt, retryKey);
          if (expired) {
            console.error(
              `[PowerSync] Grace period exceeded resolving ${conflictKind} conflict — discarding:`,
              ex,
            );
            await tx.complete();
            await discardAndCleanup(
              database,
              failedOp,
              'sync-discarded',
              `A change could not be saved and was discarded (${exMessage}). Please redo this action if it's still needed.`,
            );
          } else {
            console.error(
              `[PowerSync] Upload failed resolving ${conflictKind} conflict, will retry:`,
              ex,
            );
            throw ex;
          }
          return;
        }

        // Neither known constraint — an orders-table 23505 we don't
        // recognize. Fall back to the existing discard-as-last-resort path.
        firstFailureAt.delete(retryKey);
        await tx.complete();
        await discardAndCleanup(
          database,
          failedOp,
          'bill-conflict',
          'Bill number conflict — another device used this number. Please create the order again.',
        );
        return;
      }

      // 23505 on kitchen_tickets means the KOT was already confirmed (and its
      // per-department tickets already created) from another device before
      // this device's offline-queued insert uploaded — the UNIQUE(order_id,
      // kot_id, department_id) constraint caught the duplicate. The ticket
      // itself is not lost (the other device's row already exists server
      // side); just discard this device's local duplicate.
      if (
        code === '23505' &&
        failedOp !== null &&
        failedOp.table === 'kitchen_tickets'
      ) {
        firstFailureAt.delete(retryKey);
        await tx.complete();
        await discardAndCleanup(
          database,
          failedOp,
          'kds-ticket-conflict',
          'This kitchen ticket was already created from another device — no action needed.',
        );
        return;
      }

      const isFatalCode =
        typeof code === 'string' &&
        FATAL_RESPONSE_CODES.some((r) => r.test(code));

      const { expired: gracePeriodExpired, firstFailedAt } = checkGracePeriod(
        firstFailureAt,
        retryKey,
      );

      if (isFatalCode || gracePeriodExpired) {
        // Fatal Postgres error, or this transaction has been failing for the
        // entire grace period regardless of error code — either way, never
        // retry forever. Discard, clean up the local row, and let the user
        // know so they can redo the action if it still matters.
        firstFailureAt.delete(retryKey);
        console.error(
          isFatalCode
            ? '[PowerSync] Fatal upload error — discarding:'
            : `[PowerSync] Upload retry grace period (${UPLOAD_RETRY_GRACE_PERIOD_MS / 60000}min) exceeded — discarding:`,
          lastOp,
          ex,
        );
        await tx.complete();
        await discardAndCleanup(
          database,
          failedOp,
          'sync-discarded',
          `A change could not be saved and was discarded (${exMessage}). Please redo this action if it's still needed.`,
        );
      } else {
        // Reached the server but failed for a reason we don't recognize as
        // fatal — could be a resolvable external condition (e.g. an
        // accounting period not open yet). PowerSync retries after backoff.
        console.error(
          `[PowerSync] Upload failed, will retry (failing since ${new Date(firstFailedAt).toISOString()}):`,
          ex,
        );
        throw ex;
      }
    }
  }
}
